import toast from "react-hot-toast";
import { applyBulkMergeData, withWordContextWrite, withWordContextRead } from "./document";
import {
  E2E_COMMENT_SNAPSHOT_SCHEMA,
  E2eBulkMergeErrorReport,
  E2eCommentIdentity,
  E2eCommentIdentitySchema,
} from "./e2e-test-hook-contract";
import { toError } from "./errors";
import { bookmarkAllParagraphs } from "./paragraph-bookmarks";

const FLAT_OPC_PART_TAG = "pkg:part";
const FLAT_OPC_NAME_ATTRIBUTE = "pkg:name";
const COMMENTS_PART_NAME = "/word/comments.xml";
const COMMENTS_EXTENDED_PART_NAME = "/word/commentsExtended.xml";
const COMMENTS_IDS_PART_NAME = "/word/commentsIds.xml";

function findFlatOpcPart(flatOpc: Document, partName: string): Element | undefined {
  return Array.from(flatOpc.getElementsByTagName(FLAT_OPC_PART_TAG)).find(
    (part) => part.getAttribute(FLAT_OPC_NAME_ATTRIBUTE) === partName,
  );
}

function readOoxmlComments(flatOpc: Document): E2eCommentIdentity["ooxmlComments"] {
  const part = findFlatOpcPart(flatOpc, COMMENTS_PART_NAME);
  if (!part) return [];
  return Array.from(part.getElementsByTagName("w:comment")).map((comment) => {
    const paragraphs = Array.from(comment.getElementsByTagName("w:p"));
    const lastParagraph = paragraphs[paragraphs.length - 1];
    return {
      wId: comment.getAttribute("w:id"),
      lastParagraphParaId: lastParagraph?.getAttribute("w14:paraId") ?? null,
      text: comment.textContent ?? "",
    };
  });
}

function readCommentsExtended(flatOpc: Document): E2eCommentIdentity["commentsExtended"] {
  const part = findFlatOpcPart(flatOpc, COMMENTS_EXTENDED_PART_NAME);
  if (!part) return [];
  return Array.from(part.getElementsByTagName("w15:commentEx")).map((entry) => ({
    paraId: entry.getAttribute("w15:paraId"),
    paraIdParent: entry.getAttribute("w15:paraIdParent"),
    done: entry.getAttribute("w15:done"),
  }));
}

function readCommentsIds(flatOpc: Document): E2eCommentIdentity["commentsIds"] {
  const part = findFlatOpcPart(flatOpc, COMMENTS_IDS_PART_NAME);
  if (!part) return [];
  return Array.from(part.getElementsByTagName("w16cid:commentId")).map((entry) => ({
    paraId: entry.getAttribute("w16cid:paraId"),
    durableId: entry.getAttribute("w16cid:durableId"),
  }));
}

function causeMessageOf(error: unknown): string | undefined {
  if (!(error instanceof Error) || error.cause === undefined) return undefined;
  return toError(error.cause).message;
}

export function installE2eTestHook(): void {
  if (typeof window === "undefined") return;
  window.__e2eWordOps = {
    insertParagraph: async (text) => {
      await withWordContextWrite(async (ctx) => {
        ctx.document.body.insertParagraph(text, Word.InsertLocation.end);
        await ctx.sync();
      });
      await bookmarkAllParagraphs();
    },
    insertParagraphs: async (texts) => {
      await withWordContextWrite(async (ctx) => {
        for (const text of texts) {
          ctx.document.body.insertParagraph(text, Word.InsertLocation.end);
        }
        await ctx.sync();
      });
      await bookmarkAllParagraphs();
      await withWordContextWrite(async (ctx) => {
        ctx.document.save();
        await ctx.sync();
      });
    },
    clear: () =>
      withWordContextWrite(async (ctx) => {
        ctx.document.body.getTrackedChanges().acceptAll();
        ctx.document.changeTrackingMode = Word.ChangeTrackingMode.off;
        ctx.document.body.clear();
        await ctx.sync();
      }),
    selectText: (query) =>
      withWordContextWrite(async (ctx) => {
        const matches = ctx.document.body.search(query, { matchCase: false });
        ctx.load(matches, "items");
        await ctx.sync();
        if (matches.items.length === 0) return false;
        matches.items[0].select();
        await ctx.sync();
        return true;
      }),
    getBodyText: () =>
      withWordContextRead(async (ctx) => {
        const body = ctx.document.body;
        ctx.load(body, "text");
        await ctx.sync();
        return body.text;
      }),
    getTrackedChangeAuthors: () =>
      withWordContextRead(async (ctx) => {
        const changes = ctx.document.body.getTrackedChanges();
        ctx.load(changes, "items/author");
        await ctx.sync();
        return changes.items.map((c) => c.author);
      }),
    insertComment: (anchorText, commentText, rangeEndAnchorText) =>
      withWordContextWrite(async (ctx) => {
        const matches = ctx.document.body.search(anchorText, { matchCase: false });
        const rangeEndMatches =
          rangeEndAnchorText === undefined
            ? null
            : ctx.document.body.search(rangeEndAnchorText, { matchCase: false });
        ctx.load(matches, "items");
        if (rangeEndMatches !== null) ctx.load(rangeEndMatches, "items");
        await ctx.sync();
        if (matches.items.length === 0) return null;
        if (rangeEndMatches !== null && rangeEndMatches.items.length === 0) return null;
        const anchorRangeSpanningBothAnchors =
          rangeEndMatches === null
            ? matches.items[0]
            : matches.items[0].expandTo(rangeEndMatches.items[0]);
        const comment = anchorRangeSpanningBothAnchors.insertComment(commentText);
        ctx.load(comment, "id");
        await ctx.sync();
        ctx.document.save();
        await ctx.sync();
        return String(comment.id);
      }),
    replyToComment: (targetCommentId, replyText) =>
      withWordContextWrite(async (ctx) => {
        const comments = ctx.document.body.getComments();
        ctx.load(comments, "items/id");
        await ctx.sync();
        const target = comments.items.find(
          (comment) => String(comment.id) === String(targetCommentId),
        );
        if (!target) return false;
        target.reply(replyText);
        await ctx.sync();
        ctx.document.save();
        await ctx.sync();
        return true;
      }),
    resolveComment: (targetCommentId) =>
      withWordContextWrite(async (ctx) => {
        const comments = ctx.document.body.getComments();
        ctx.load(comments, "items/id");
        await ctx.sync();
        const target = comments.items.find(
          (comment) => String(comment.id) === String(targetCommentId),
        );
        if (!target) return false;
        target.resolved = true;
        await ctx.sync();
        ctx.document.save();
        await ctx.sync();
        return true;
      }),
    getComments: () =>
      withWordContextRead(async (ctx) => {
        const comments = ctx.document.body.getComments();
        ctx.load(comments, "items/id,items/content,items/replies/items/id,items/replies/items/content");
        await ctx.sync();
        return E2E_COMMENT_SNAPSHOT_SCHEMA.array().parse(
          comments.items.map((comment) => ({
            id: comment.id,
            text: comment.content,
            replies: comment.replies.items.map((reply) => ({ id: reply.id, text: reply.content })),
          })),
        );
      }),
    readCommentIdentity: () =>
      withWordContextWrite(async (ctx) => {
        ctx.document.save();
        await ctx.sync();

        const comments = ctx.document.body.getComments();
        ctx.load(comments, "items/id,items/content,items/replies/items/id,items/replies/items/content");
        const ooxml = ctx.document.body.getOoxml();
        await ctx.sync();

        // eslint-disable-next-line office-addins/call-sync-before-read
        const flatOpc = new DOMParser().parseFromString(ooxml.value, "text/xml");

        return E2eCommentIdentitySchema.parse({
          officeJs: comments.items.map((comment) => ({
            id: comment.id,
            text: comment.content,
            replies: comment.replies.items.map((reply) => ({ id: reply.id, text: reply.content })),
          })),
          packageParts: Array.from(flatOpc.getElementsByTagName(FLAT_OPC_PART_TAG))
            .map((part) => part.getAttribute(FLAT_OPC_NAME_ATTRIBUTE) ?? "")
            .filter(Boolean),
          ooxmlComments: readOoxmlComments(flatOpc),
          commentsExtended: readCommentsExtended(flatOpc),
          commentsIds: readCommentsIds(flatOpc),
        });
      }),
    applyBulkMergeData: async (mergeData) => {
      const errorReports: E2eBulkMergeErrorReport[] = [];
      const result = await applyBulkMergeData({
        mergeData,
        applySource: { initiatedFrom: "bulk", reviewId: null },
        sendError: (error, context) =>
          errorReports.push({ errorMessage: toError(error).message, causeMessage: causeMessageOf(error), context }),
      });
      return { ...result, errorReports };
    },
    setCustomProperties: (properties) =>
      withWordContextWrite(async (ctx) => {
        for (const [propertyName, propertyValue] of Object.entries(properties)) {
          ctx.document.properties.customProperties.add(propertyName, propertyValue);
        }
        await ctx.sync();
      }),
    deleteCustomProperties: (propertyNames) =>
      withWordContextWrite(async (ctx) => {
        const customProperties = ctx.document.properties.customProperties;
        ctx.load(customProperties, "items/key");
        await ctx.sync();
        customProperties.items
          .filter((customProperty) => propertyNames.includes(customProperty.key))
          .forEach((customProperty) => customProperty.delete());
        await ctx.sync();
      }),
    dismissToasts: () => {
      toast.remove();
    },
    selectConversation: () => {},
    currentConversationId: undefined,
    currentSelectionText: undefined,
  };
}

export function setE2eConversationSelector(selectConversation: (conversationId: string) => void): void {
  if (typeof window !== "undefined" && window.__e2eWordOps) {
    window.__e2eWordOps.selectConversation = selectConversation;
  }
}

export function setE2eConversationId(id: string | undefined): void {
  if (typeof window !== "undefined" && window.__e2eWordOps) {
    window.__e2eWordOps.currentConversationId = id;
  }
}

export function setE2eSelectionText(text: string | undefined): void {
  if (typeof window !== "undefined" && window.__e2eWordOps) {
    window.__e2eWordOps.currentSelectionText = text;
  }
}
